// Particles (RALLY_GOAL.md §11): CPU-simulated, GPU-instanced sprites (identical on WebGPU and
// WebGL2). Each layer is a ring of preallocated arrays; dead particles are swap-removed so the
// live ones stay contiguous and the draw uses `count`.
import * as THREE from 'three/webgpu';
import type { Node } from 'three/webgpu';
import { instancedBufferAttribute, positionGeometry, uniform, float, smoothstep, hash, instanceIndex, sin, mix, varying, cameraPosition } from 'three/tsl';

export type SpriteShape = 'puff' | 'chip' | 'flame';

export interface LayerOptions {
  capacity: number;
  /** Procedural sprite shape (computed from the sprite UV; no texture needed). */
  shape: SpriteShape;
  additive?: boolean;
  /** m/s² downward. */
  gravity: number;
  /** Linear drag (1/s). */
  drag: number;
  /** Fraction of the wind velocity a particle drifts with. */
  windFollow: number;
  /** Alpha curve: fade-in fraction of life. */
  fadeIn: number;
  /** Shaded by the scene light level (dust, smoke, spray); flames are emissive. */
  lit: boolean;
  /** Keep the sprite's lower edge above the ground it was spawned from (fraction of its size). */
  floorLift?: number;
}

/** Instances always drawn (see update): keeps the mesh on one pipeline variant. */
const MIN_DRAWN = 2;

export class ParticleLayer {
  readonly mesh: THREE.Mesh;
  private readonly cap: number;
  private n = 0;
  private readonly p: Float32Array;
  private readonly v: Float32Array;
  private readonly age: Float32Array;
  private readonly life: Float32Array;
  private readonly s0: Float32Array;
  private readonly s1: Float32Array;
  private readonly gy: Float32Array;
  private readonly rgba: Float32Array;
  private readonly posAttr: THREE.InstancedBufferAttribute;
  private readonly sizeAttr: THREE.InstancedBufferAttribute;
  private readonly colAttr: THREE.InstancedBufferAttribute;
  private readonly opt: LayerOptions;
  /** Overall light level (sun/sky) applied to lit layers. */
  static readonly light = uniform(new THREE.Color(1, 1, 1));

  constructor(opt: LayerOptions) {
    this.opt = opt;
    const cap = (this.cap = opt.capacity);
    this.p = new Float32Array(cap * 3);
    this.v = new Float32Array(cap * 3);
    this.age = new Float32Array(cap);
    this.life = new Float32Array(cap);
    this.s0 = new Float32Array(cap);
    this.s1 = new Float32Array(cap);
    this.gy = new Float32Array(cap);
    this.rgba = new Float32Array(cap * 4);
    this.posAttr = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.sizeAttr = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1);
    this.colAttr = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    const m = new THREE.SpriteNodeMaterial({ transparent: true, depthWrite: false });
    const centre = instancedBufferAttribute(this.posAttr);
    m.positionNode = centre;
    m.scaleNode = instancedBufferAttribute(this.sizeAttr);
    const col = instancedBufferAttribute(this.colAttr) as unknown as Node<'vec4'>;
    const lightN = opt.lit ? ParticleLayer.light : uniform(new THREE.Color(1, 1, 1));
    // Quad-local position (−0.5…0.5) as an explicit varying: uv() evaluates as a constant for these
    // count-instanced sprites in r186.
    const q = varying(positionGeometry.xy);
    const d = q.length().mul(2);
    let alpha: Node<'float'>;
    let shade: Node<'float'>;
    if (opt.shape === 'chip') {
      alpha = float(1).sub(smoothstep(float(0.7), float(0.9), d));
      shade = float(1).sub(d.mul(0.35));
    } else if (opt.shape === 'flame') {
      alpha = float(1).sub(smoothstep(float(0.0), float(1.0), d)).pow(1.6);
      shade = mix(float(2.2), float(0.6), d);
    } else {
      // Lumpy puff: radial falloff with a per-particle wobble so overlapping puffs don't tile.
      const ph = hash(instanceIndex).mul(6.283);
      const a = q;
      const ang = a.y.atan(a.x);
      const wob = sin(ang.mul(3).add(ph)).mul(0.08).add(sin(ang.mul(5).sub(ph.mul(1.7))).mul(0.05));
      alpha = float(1).sub(smoothstep(float(0.15), float(0.95).add(wob), d)).pow(1.4);
      shade = float(1.05).sub(d.mul(0.2));
    }
    if (opt.shape === 'puff') {
      // Fade sprites right in front of the camera instead of filling the screen with a sheet.
      const camD = varying(centre as unknown as Node<'vec3'>).sub(cameraPosition).length();
      alpha = alpha.mul(smoothstep(float(2.0), float(7.5), camD));
    }
    m.colorNode = col.rgb.mul(lightN).mul(shade);
    m.opacityNode = alpha.mul(col.a);
    if (opt.additive) {
      m.blending = THREE.AdditiveBlending;
      m.fog = false;
    }
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), m);
    this.mesh.frustumCulled = false;
    this.mesh.count = MIN_DRAWN;
    this.mesh.visible = false;
    this.mesh.renderOrder = opt.additive ? 3 : 2;
  }

  get alive(): number {
    return this.n;
  }

  spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, size0: number, size1: number, r: number, g: number, b: number, a: number): void {
    let i = this.n;
    if (i >= this.cap) {
      // Full: recycle the oldest-looking slot (cheap: the first one).
      i = 0;
    } else this.n++;
    this.p[i * 3] = x;
    this.p[i * 3 + 1] = y;
    this.p[i * 3 + 2] = z;
    this.v[i * 3] = vx;
    this.v[i * 3 + 1] = vy;
    this.v[i * 3 + 2] = vz;
    this.age[i] = 0;
    this.life[i] = life;
    this.s0[i] = size0;
    this.s1[i] = size1;
    this.gy[i] = y;
    this.rgba[i * 4] = r;
    this.rgba[i * 4 + 1] = g;
    this.rgba[i * 4 + 2] = b;
    this.rgba[i * 4 + 3] = a;
  }

  clear(): void {
    this.n = 0;
    this.mesh.visible = false;
  }

  update(dt: number, windX: number, windZ: number): void {
    // Everything the loop reads goes into locals first: the six layers' option objects have
    // different shapes, and a polymorphic read of a number field inside the loop boxed it per
    // particle (~0.5 MB/s of garbage).
    const o = this.opt;
    const wf = +o.windFollow;
    const grav = +o.gravity;
    const lift = o.floorLift === undefined ? -1 : +o.floorLift;
    const fadeIn = Math.max(0.001, +o.fadeIn);
    const P = this.posAttr.array as Float32Array;
    const S = this.sizeAttr.array as Float32Array;
    const C = this.colAttr.array as Float32Array;
    const p = this.p;
    const v = this.v;
    const ages = this.age;
    const life = this.life;
    const s0 = this.s0;
    const s1 = this.s1;
    const gy = this.gy;
    const rgba = this.rgba;
    const dragK = Math.exp(-o.drag * dt);
    const wx = windX * wf;
    const wz = windZ * wf;
    let n = this.n;
    let i = 0;
    while (i < n) {
      const age = (ages[i] += dt);
      if (age >= life[i]) {
        // Swap-remove.
        const last = --n;
        if (i !== last) {
          for (let k = 0; k < 3; k++) {
            p[i * 3 + k] = p[last * 3 + k];
            v[i * 3 + k] = v[last * 3 + k];
          }
          for (let k = 0; k < 4; k++) rgba[i * 4 + k] = rgba[last * 4 + k];
          ages[i] = ages[last];
          life[i] = life[last];
          s0[i] = s0[last];
          s1[i] = s1[last];
          gy[i] = gy[last];
        }
        continue;
      }
      const j = i * 3;
      v[j] = (v[j] - wx) * dragK + wx;
      v[j + 1] = v[j + 1] * dragK - grav * dt;
      v[j + 2] = (v[j + 2] - wz) * dragK + wz;
      p[j] += v[j] * dt;
      p[j + 1] += v[j + 1] * dt;
      p[j + 2] += v[j + 2] * dt;
      const t = age / life[i];
      const grow = 1 - (1 - t) * (1 - t);
      const size = s0[i] + (s1[i] - s0[i]) * grow;
      if (lift >= 0) {
        const floor = gy[i] + size * lift;
        if (p[j + 1] < floor) p[j + 1] = floor;
      }
      P[j] = p[j];
      P[j + 1] = p[j + 1];
      P[j + 2] = p[j + 2];
      // Size eases out (fast growth, then slow), alpha fades in then out.
      S[i] = size;
      const fade = Math.min(1, t / fadeIn) * (1 - t) * (1 - t * 0.3);
      const c = i * 4;
      C[c] = rgba[c];
      C[c + 1] = rgba[c + 1];
      C[c + 2] = rgba[c + 2];
      C[c + 3] = rgba[c + 3] * fade;
      i++;
    }
    this.n = n;
    // At least MIN_DRAWN instances, the rest zero-sized: three keys a mesh's pipeline on whether its
    // count is above 1, and drops the unused variant, so a layer flickering between one particle and
    // many (or emptied by a restart) would recompile its shader mid-stage.
    for (let k = this.n; k < MIN_DRAWN; k++) S[k] = 0;
    const drawn = Math.max(this.n, MIN_DRAWN);
    this.mesh.count = drawn;
    // three still issues a draw for count 0; skip the layer while it is empty.
    this.mesh.visible = this.n > 0;
    setRange(this.posAttr, drawn * 3);
    setRange(this.sizeAttr, drawn);
    setRange(this.colAttr, drawn * 4);
  }
}

/**
 * Upload only the live prefix of an attribute, reusing one range object (no per-frame garbage).
 * The attribute keeps that range for good: three clears ranges after each upload with
 * `length = 0`, which drops V8's backing store, so the next push would reallocate it every frame.
 */
const ranges = new WeakMap<THREE.BufferAttribute, { start: number; count: number }>();
const keepRanges = (): void => {};
export function setRange(a: THREE.BufferAttribute, count: number): void {
  let r = ranges.get(a);
  if (!r) {
    ranges.set(a, (r = { start: 0, count: 0 }));
    a.updateRanges.length = 0;
    a.updateRanges.push(r);
    a.clearUpdateRanges = keepRanges;
  }
  r.count = count;
  a.needsUpdate = true;
}
